import { metaDelete, metaGet, metaSet } from './db';

/**
 * ПИН-ът НЕ е парола към сървъра и никога не напуска телефона (ADR 0005).
 * Той отключва дълготрайния токен на устройството, който стои на диска
 * шифрован с AES-GCM и ключ, изведен от ПИН-а с PBKDF2.
 *
 * Открадналият телефона вижда само шифрован блок; десет сгрешени ПИН-а
 * изтриват токена и телефонът трябва да се свърже отново с код от диспечера.
 */

const VAULT_KEY = 'auth.vault';
const FAILS_KEY = 'auth.pinFails';

export const MAX_PIN_ATTEMPTS = 10;
const PBKDF2_ITERATIONS = 210_000;

interface Vault {
  salt: string;
  iv: string;
  data: string;
  createdAt: string;
}

function toBase64(bytes: Uint8Array): string {
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary);
}

function fromBase64(value: string): Uint8Array {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

export function isCryptoAvailable(): boolean {
  return typeof globalThis.crypto?.subtle?.importKey === 'function';
}

async function deriveKey(pin: string, salt: Uint8Array): Promise<CryptoKey> {
  const material = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(pin),
    'PBKDF2',
    false,
    ['deriveKey'],
  );
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: salt as BufferSource, iterations: PBKDF2_ITERATIONS, hash: 'SHA-256' },
    material,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

export async function storeToken(pin: string, refreshToken: string): Promise<void> {
  if (!isCryptoAvailable()) {
    throw new Error('Приложението трябва да е отворено през защитен адрес (https).');
  }
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = await deriveKey(pin, salt);
  const encrypted = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: iv as BufferSource },
    key,
    new TextEncoder().encode(refreshToken),
  );
  const vault: Vault = {
    salt: toBase64(salt),
    iv: toBase64(iv),
    data: toBase64(new Uint8Array(encrypted)),
    createdAt: new Date().toISOString(),
  };
  await metaSet(VAULT_KEY, vault);
  await metaSet(FAILS_KEY, 0);
}

export async function hasVault(): Promise<boolean> {
  return (await metaGet<Vault>(VAULT_KEY)) !== undefined;
}

export interface UnlockResult {
  ok: boolean;
  refreshToken?: string;
  attemptsLeft: number;
  wiped: boolean;
}

export async function unlock(pin: string): Promise<UnlockResult> {
  const vault = await metaGet<Vault>(VAULT_KEY);
  if (!vault) return { ok: false, attemptsLeft: MAX_PIN_ATTEMPTS, wiped: false };

  const salt = fromBase64(vault.salt);
  const iv = fromBase64(vault.iv);
  try {
    const key = await deriveKey(pin, salt);
    const plain = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: iv as BufferSource },
      key,
      fromBase64(vault.data) as BufferSource,
    );
    await metaSet(FAILS_KEY, 0);
    return {
      ok: true,
      refreshToken: new TextDecoder().decode(plain),
      attemptsLeft: MAX_PIN_ATTEMPTS,
      wiped: false,
    };
  } catch {
    // Неуспешното разшифроване е сгрешен ПИН — AES-GCM проверява етикета сам.
    const fails = ((await metaGet<number>(FAILS_KEY)) ?? 0) + 1;
    await metaSet(FAILS_KEY, fails);
    if (fails >= MAX_PIN_ATTEMPTS) {
      await metaDelete(VAULT_KEY);
      await metaSet(FAILS_KEY, 0);
      return { ok: false, attemptsLeft: 0, wiped: true };
    }
    return { ok: false, attemptsLeft: MAX_PIN_ATTEMPTS - fails, wiped: false };
  }
}

export async function forgetToken(): Promise<void> {
  await metaDelete(VAULT_KEY);
  await metaSet(FAILS_KEY, 0);
}

export async function attemptsLeft(): Promise<number> {
  return MAX_PIN_ATTEMPTS - ((await metaGet<number>(FAILS_KEY)) ?? 0);
}
